import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Crown } from "lucide-react";
import { api } from "@/lib/api";

const MAX_ATTEMPTS = 8;
const INTERVAL = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | success | pending | error
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;

    const poll = async (attempt) => {
      if (cancelled) return;
      if (attempt >= MAX_ATTEMPTS) {
        setState("pending");
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setDetail(data);
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
        setTimeout(() => poll(attempt + 1), INTERVAL);
      } catch (e) {
        setTimeout(() => poll(attempt + 1), INTERVAL);
      }
    };
    poll(0);
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-wine-deep px-6 grain relative overflow-hidden">
      <div className="pointer-events-none absolute -top-40 right-0 h-[500px] w-[500px] rounded-full bg-gold/10 blur-[130px]" />
      <div className="relative z-10 max-w-lg w-full text-center" data-testid="payment-success-page">
        {state === "polling" && (
          <>
            <Loader2 className="mx-auto h-14 w-14 text-gold animate-spin" />
            <h1 className="font-display text-4xl text-cream mt-6">Confirming your payment…</h1>
            <p className="text-cream/60 mt-3">Hang tight, this only takes a moment.</p>
          </>
        )}

        {state === "success" && (
          <>
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-gold">
              <Crown className="h-10 w-10 text-wine-deep" />
            </div>
            <h1 className="font-display text-5xl text-cream mt-8">You're Crowned. 👑</h1>
            <p className="text-cream/70 mt-4 text-lg leading-relaxed">
              Your seat in the Crowned™ Pro — Level 1 founding cohort is secured. A confirmation email
              is on its way with your next steps. We can't wait to see you on September 15.
            </p>
            {detail && (
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold mt-6">
                Paid ${(detail.amount_total / 100).toFixed(2)} {String(detail.currency).toUpperCase()}
              </p>
            )}
            <Link
              to="/"
              data-testid="back-home"
              className="inline-flex mt-10 rounded-full bg-gold px-8 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-wine-deep hover:bg-cream transition-colors duration-300"
            >
              Back to Home
            </Link>
          </>
        )}

        {state === "pending" && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
            <h1 className="font-display text-4xl text-cream mt-6">Almost there</h1>
            <p className="text-cream/60 mt-3">
              Your payment is still processing. You'll receive a confirmation email shortly — no need
              to pay again.
            </p>
            <Link
              to="/"
              className="inline-flex mt-8 rounded-full border border-cream/25 px-8 py-3.5 font-mono text-[12px] uppercase tracking-[0.18em] text-cream hover:border-gold hover:text-gold transition-colors duration-300"
            >
              Back to Home
            </Link>
          </>
        )}

        {state === "error" && (
          <>
            <XCircle className="mx-auto h-14 w-14 text-cream/60" />
            <h1 className="font-display text-4xl text-cream mt-6">Something went wrong</h1>
            <p className="text-cream/60 mt-3">
              We couldn't confirm this payment. If you were charged, please contact us and we'll sort
              it out right away.
            </p>
            <Link
              to="/"
              className="inline-flex mt-8 rounded-full bg-gold px-8 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-wine-deep hover:bg-cream transition-colors duration-300"
            >
              Back to Home
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
